/**
 * Hero Animations using GSAP ScrollTrigger & GSAP SplitText
 */

export class HeroAnimations {
  constructor() {
    this.init();
  }

  /**
   * Initialize animations
   */
  init() {
    if (
      typeof gsap === "undefined" ||
      typeof ScrollTrigger === "undefined" ||
      typeof SplitText === "undefined"
    ) {
      console.warn("GSAP, ScrollTrigger, or SplitText not loaded");
      document.documentElement.classList.add("wps-hero-fallback");
      return;
    }

    gsap.registerPlugin(ScrollTrigger, SplitText);
    this.mm = gsap.matchMedia();

    this.homeHeroAnimations();
  }

  /**
   * Run load animation for a single hero section
   *
   * @param {HTMLElement} hero Hero wrapper element.
   * @param {Object} selectors Element selectors scoped to the hero.
   * @param {Object} options Animation options.
   * @param {boolean} options.includeAwards Whether to animate award items.
   * @param {boolean} options.animateNavbar Whether to animate the navbar.
   * @return {boolean} Whether navbar was animated.
   */
  
  animateHeroLoad(hero, selectors, options = {}) {
    const { includeAwards = false, animateNavbar = false } = options;
    const navbar = document.querySelector(".wps-header-navbar__wrapper");
    const title = hero.querySelector(selectors.title);
    const descriptionItems = hero.querySelectorAll(selectors.descriptionItems);
    const heroImage = hero.querySelector(selectors.heroImage);
    const pattern = selectors.pattern
      ? hero.querySelector(selectors.pattern)
      : null;
    const awards =
      includeAwards && selectors.awards
        ? hero.querySelectorAll(selectors.awards)
        : [];

    if (animateNavbar && navbar) {
      gsap.set(navbar, { opacity: 0, y: -20 });
    }
    if (title) {
      gsap.set(title, { opacity: 0, y: 90 });
    }
    if (descriptionItems.length) {
      gsap.set(descriptionItems, { opacity: 0, y: 40 });
    }
    if (heroImage) {
      gsap.set(heroImage, { opacity: 0, y: 70 });
    }
    if (awards.length) {
      gsap.set(awards, { opacity: 0, y: 20 });
    }

    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out",
        duration: 0.9,
      },
    });

    if (animateNavbar && navbar) {
      tl.to(navbar, { opacity: 1, y: 0 }, 0);
    }
    if (title) {
      tl.to(title, { opacity: 1, y: 0 }, 0.2);
    }
    if (descriptionItems.length) {
      tl.to(
        descriptionItems,
        {
          opacity: 1,
          y: 0,
          stagger: 0.15,
        },
        0.5
      );
    }
    if (heroImage) {
      tl.to(
        heroImage,
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
        },
        0.6
      );
    }
    if (pattern) {
      tl.from(
        pattern,
        {
          opacity: 0,
          scale: 0.8,
          duration: 1.2,
        },
        0.6
      );
    }
    if (awards.length) {
      tl.to(
        awards,
        {
          opacity: 1,
          y: 0,
          stagger: 0.12,
        },
        0.9
      );
    }

    document.documentElement.classList.add("wps-hero-animated");

    return animateNavbar && !!navbar;
  }

  /**
   * Home Hero Animations on load
   */
  homeHeroAnimations() {
    const heroes = [
      {
        element: document.querySelector(".wps-header-hero__wrapper"),
        selectors: {
          title: ".title",
          descriptionItems: ".description__container .content > *",
          heroImage: ".image__container img",
          awards: ".awards__container .award",
          pattern: ".pattern__container .pattern",
        },
        includeAwards: true,
      },
      {
        element: document.querySelector(
          ".wps-second-opinion-header-hero__wrapper"
        ),
        selectors: {
          title: ".content__container .page-title",
          descriptionItems:
            ".content__container .header-page-description, .content__container .header-cta-buttons",
          heroImage: ".image__container img",
          pattern: ".pattern__container .pattern",
        },
        includeAwards: false,
      },
    ];

    let navbarAnimated = false;

    heroes.forEach((config) => {
      if (!config.element) {
        return;
      }

      const didAnimateNavbar = this.animateHeroLoad(
        config.element,
        config.selectors,
        {
          includeAwards: config.includeAwards,
          animateNavbar: !navbarAnimated,
        }
      );

      if (didAnimateNavbar) {
        navbarAnimated = true;
      }
    });
  }
}
